import { Component, OnInit } from '@angular/core';
import { MoreButtonComponent } from '../../../core/buttons/more-button.component';
import { ClaimLabelComponent } from '../../../core/labels/claim-label.component';
import { TagComponent, TagType } from '../../../core/labels/tag.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { BaseComponent } from '../../../base/base.component';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { ClaimGLClaimCard } from '../../../../models/api/view/claim/summary/claim-gl-claim-card';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../../core/panels/loading-panel.component';
import { ClaimFlagsComponent } from './claim-flags.component';
import { getFieldVisibility } from '../../../../functions/field-security';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-gl-claim-card',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    TagComponent,
    ActionRowComponent,
    MoreButtonComponent,
    ClaimLabelComponent,
    LoadingPanelComponent,
    ClaimFlagsComponent,
  ],
  template: `
    <app-loading-panel
      [state]="loadingState"
      [loadingHeight]="300"
      loadingStyle="Box"
      [error]="error"
    >
      <ng-template #loaded>
        @if (glClaimCard) {
          <div class="tw-w-full">
            <div
              class="tw-flex tw-flex-col md:tw-flex-row tw-gap-0 md:tw-gap-8 tw-w-full"
            >
              <app-detail-panel [dataSource]="glClaimCard" class="tw-w-full ">
                <app-detail-item label="Claim Number" name="claimNumber">
                  <ng-template let-data #value>
                    <app-claim-label [claim]="claimRow" [readOnly]="true" />
                  </ng-template>
                </app-detail-item>
                <app-detail-item label="Status">
                  <ng-template let-data #value>
                    <app-tag [type]="statusType">{{ statusDisplay }}</app-tag>
                  </ng-template>
                </app-detail-item>
                <app-detail-item
                  label="Date of Loss"
                  name="dateOfLoss"
                  format="dt"
                />
                <app-detail-item label="Jurisdiction" name="jurisdiction" />
              </app-detail-panel>

              <app-detail-panel [dataSource]="injuryData" class="tw-w-full">
                @if (visibility?.showCause) {
                  <app-detail-item label="Cause" name="cause" />
                }
                @if (visibility?.showNatureResult) {
                  <app-detail-item
                    label="Nature/result"
                    name="nature"
                    labelCase="none"
                  />
                }
                @if (visibility?.showPartTarget) {
                  <app-detail-item
                    label="Part/target"
                    name="part"
                    labelCase="none"
                  />
                }
                @if (glClaimCard.showBodySide) {
                  <app-detail-item
                    label="Body Side"
                    name="bodySide"
                    labelCase="none"
                  />
                }
              </app-detail-panel>
            </div>

            @if (
              visibility?.showEventDescription && glClaimCard.eventDescription
            ) {
              <div>
                <div class="tw-font-bold tw-py-2">Event description</div>
                <div class="tw-pb-4 tw-whitespace-pre-line">
                  {{ glClaimCard.eventDescription }}
                </div>
              </div>
            }

            <app-action-row>
              <app-claim-flags left1 [flags]="glClaimCard" />
              @if (this.detailAuth) {
                <app-more-button
                  right1
                  (click)="
                    this.view.navigateCurrentClaim(['general-liability'])
                  "
                  [ariaLabel]="
                    'Navigate to General Liability Details for ' +
                    glClaimCard?.claimNumber
                  "
                />
              }
            </app-action-row>
          </div>
        }
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class GlClaimCardComponent extends BaseComponent implements OnInit {
  claimRow: ClaimRow;
  glClaimCard: ClaimGLClaimCard;
  detailAuth = false;
  statusType: TagType = 'Success';
  statusDisplay = '';
  loadingState: LoadingState = 'Loading';
  error: any = null;
  injuryData: any = {};
  visibility: ReturnType<typeof getFieldVisibility>;

  @HandleLoadingState({
    loadingProperty: 'loadingState',
    onValue: 'Loading',
    offValue: 'Loaded',
  })
  async ngOnInit(): Promise<void> {
    this.claimRow = this.view.currentClaim();
    this.detailAuth = this.view.authorize('Details');

    this.glClaimCard = await this.api.claimSummary.getGLClaimCard(
      this.claimRow.recordSource,
      this.claimRow.recordId,
    );

    this.statusDisplay =
      this.glClaimCard.status +
      (this.glClaimCard.subStatus ? ' - ' + this.glClaimCard.subStatus : '');
    this.statusType = this.getStatusType(this.glClaimCard.status);

    this.injuryData = {
      cause: this.glClaimCard.cause || '',
      nature: this.glClaimCard.natureResult || '',
      part: this.glClaimCard.partTarget || '',
      bodySide: this.glClaimCard.bodySide || '',
    };

    this.visibility = getFieldVisibility(
      this.session.user?.fieldSecurity?.restrictedFields,
    );
  }

  private getStatusType(status: string): TagType {
    const statusUpper = status?.toUpperCase() || '';
    if (statusUpper.includes('OPEN') || statusUpper.includes('REOPEN')) {
      return 'Success';
    }
    if (statusUpper.includes('CLOSED')) {
      return 'Error';
    }
    if (statusUpper.includes('INCIDENT')) {
      return 'Warn';
    }
    return 'Success';
  }
}
